import { inject } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { ILocalization, LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { UIPageStore } from 'stores/ui-page-store';

import { MessagingCardViewModel } from 'models';

import { UitkCardContent } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType } from 'uitk-react-images';
import { UitkHeading } from 'uitk-react-text';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';
import { UitkMark, UitkMarkSize } from 'uitk-react-mark';
import { UitkMessagingCard, UitkMessagingCardMedia } from 'uitk-react-messaging-cards';

import { DoubleLink } from 'components/common/media/double-link';
import { KNOWN_ACTIONS, MessagingCardActions } from 'components/common/messaging-card/messaging-card-actions';
import { MessageType } from '__generated__/api';
import { MoreDetailsLx } from 'components/common/messaging-card/more-details-lx';
import { TermsAndConditionsDialog } from 'components/common/messaging-card/terms-and-conditions-dialog';

const LinkTracked = ClickTracker(DoubleLink);
export interface MessagingCardProps extends LocalizedComponentProps {
  routeStore?: RouteStore;
  analytics?: AnalyticsStore;
  className?: string;
  liveAnnounceLevel?: string;
  model: MessagingCardViewModel;
  tag?: string | React.ComponentClass | React.FunctionComponent;
  index?: number;
  uiPage?: UIPageStore;
  testId?: string;
  withBorder?: boolean;
  iconSize?: UitkIconSize;
}

const getMarkModel = model => {
  if (model && model.titleModel != null && model.titleModel.mark != null) {
    return model.titleModel;
  }
  if (model && model.subtitleModel != null && model.subtitleModel.mark != null) {
    return model.subtitleModel;
  }

  return null;
};
@withLocalization
@inject('analytics', 'uiPage', 'routeStore')
export class MessagingCard extends React.PureComponent<MessagingCardProps> {
  public static defaultProps: Partial<MessagingCardProps> = {
    className: 'uitk-cell xl-cell-1-3 l-cell-1-3 m-cell-1-2 s-cell-1-1',
    liveAnnounceLevel: 'polite',
    tag: 'li',
  };

  public render() {
    const {
      model,
      analytics,
      className: tagClassName,
      liveAnnounceLevel,
      tag: Tag,
      routeStore,
      uiPage,
      l10n,
      testId,
      iconSize = UitkIconSize.MEDIUM,
      withBorder = false,
    } = this.props;

    if (!model) {
      return null;
    }

    const illustration = model.titleModel?.illustration;
    const icon = model.titleModel?.icon;
    const modelWithMark = getMarkModel(model);
    const defaultWrapperProps = {
      testId,
      Tag,
      liveAnnounceLevel,
      tagClassName,
      iconSize,
    };

    const iconClasses = 'messaging-card__title-icon all-l-margin-three all-cell-align-middle';
    const markSize = model.type === MessageType.LOGIN ? UitkMarkSize.MEDIUM_LARGE : UitkMarkSize.LARGE;

    return (
      <MessagingCardWrapper {...defaultWrapperProps} withBorder={withBorder}>
        {illustration && (
          <UitkMessagingCardMedia>
            <UitkFigure ratio={UitkFigureAspectRatioType.R1_1} src={illustration} />
          </UitkMessagingCardMedia>
        )}
        {modelWithMark && modelWithMark.mark && (
          <UitkMark name={modelWithMark.mark} description={modelWithMark.text} size={markSize} className={iconClasses} />
        )}
        {icon && (
          <UitkMessagingCardMedia withBackground={true}>
            <UitkIcon name={icon} size={iconSize} />
          </UitkMessagingCardMedia>
        )}
        <MessagingCardBody analytics={analytics} model={model} routeStore={routeStore} uiPage={uiPage} l10n={l10n} />
      </MessagingCardWrapper>
    );
  }
}

export const MessagingCardWrapper = ({ Tag, liveAnnounceLevel, tagClassName, children, testId = 'messaging-card', withBorder }) => (
  <Tag className={tagClassName} aria-live={liveAnnounceLevel} data-stid={testId}>
    <UitkMessagingCard withBorder={withBorder}>
      <UitkCardContent>{children}</UitkCardContent>
    </UitkMessagingCard>
  </Tag>
);

interface MessagingCardBodyProps {
  model: MessagingCardViewModel;
  analytics?: AnalyticsStore;
  routeStore?: RouteStore;
  uiPage?: UIPageStore;
  l10n?: ILocalization;
}

const MessagingCardDialogs = ({ primaryAction, routeStore }) => {
  if (
    primaryAction &&
    primaryAction.actionDetails &&
    primaryAction.actionDetails.name &&
    primaryAction.actionDetails.name.trim().toLowerCase() === KNOWN_ACTIONS.TERMS_AND_CONDITIONS
  ) {
    return <TermsAndConditionsDialog action={primaryAction} routeStore={routeStore} />;
  } else if (
    primaryAction &&
    primaryAction.actionDetails &&
    primaryAction.actionDetails.name &&
    primaryAction.actionDetails.name.trim().toLowerCase() === KNOWN_ACTIONS.FREE_LX
  ) {
    return <MoreDetailsLx action={primaryAction} routeStore={routeStore} />;
  }

  return null;
};

const MessagingCardBody: React.FunctionComponent<MessagingCardBodyProps> = ({ analytics, model, routeStore, uiPage, l10n }) => {
  const titleModel = model.titleModel;
  const subtitleModel = model.subtitleModel;
  const title = titleModel && titleModel.text;
  const subtitle = subtitleModel && subtitleModel.text;
  const actionModel = model.actionModel || {};
  const primaryAction = actionModel.primary || {};
  const secondaryAction = actionModel.secondary || {};
  const showNotification = () => {
    if (uiPage && l10n) {
      uiPage.showNotification(l10n.formatText('coupon.copy.notification'));
    }
  };

  return (
    <>
      <MessagingCardDialogs primaryAction={primaryAction} routeStore={routeStore} />
      <div className="uitk-cell all-cell-1-2 all-cell-fill all-cell-align-middle all-y-padding-three all-x-padding-three">
        {title && (
          <UitkHeading level={3} size={300}>
            {title}
          </UitkHeading>
        )}

        {subtitle && <div className="messaging-card__subtitle uitk-type-300">{subtitle}</div>}

        {primaryAction.text && (
          <div className="all-t-padding-two uitk-type-300">
            {secondaryAction.text && (
              <LinkTracked
                to={secondaryAction.href}
                rfrr={secondaryAction.rfrr}
                linkName="Secondary Messaging Action"
                trackImmediate={true}
                className="all-r-margin-six"
                onClick={MessagingCardActions.resolveAction(
                  routeStore,
                  secondaryAction,
                  analytics,
                  showNotification,
                  actionModel.primary.rfrr
                )}
              >
                {secondaryAction.text}
              </LinkTracked>
            )}

            {primaryAction.text && (
              <LinkTracked
                to={primaryAction.href}
                rfrr={primaryAction.rfrr}
                linkName="Primary Messaging Action"
                trackImmediate={true}
                onClick={MessagingCardActions.resolveAction(routeStore, primaryAction, analytics, showNotification, primaryAction.href)}
              >
                {primaryAction.text}
              </LinkTracked>
            )}
          </div>
        )}
      </div>
    </>
  );
};
MessagingCardBody.displayName = 'MessagingCardBody';
